import { UserApi } from 'api/user/user-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { nameHelper } from 'lib/helpers/name-helper.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { map, of } from 'rxjs';
import { InstallComponent } from 'src/app/modules/install/install.component.js';
import { SubscribeComponent } from 'src/app/modules/subscription/subscribe.component.js';
import { AccountReadyComponent } from 'src/app/modules/users/account-ready/account-ready.component.js';
import { EditMyAvatarComponent } from 'src/app/modules/users/edit-my-avatar.component.js';
import { EditMySettingsComponent } from 'src/app/modules/users/edit-my-settings.component.js';
import { CoreAppComponentDependencies } from '../../core/core-app-dependencies.js';
import { EditUserPasswordComponent } from '../../modules/users/edit-user-password.component.js';
import { MyNotificationsComponent } from '../../modules/users/my-notifications.component.js';
import {
	ICoreRoutes,
	IMenuItem,
	IPageMetadata,
	IPageTag,
	ISharedData,
	ITypePageCard,
	ITypeRoute,
	ITypeRouteAction,
	RouteInitializer
} from '../../pages/type-routes/type-route.models.js';

export interface IProfileRoutes extends ICoreRoutes {
	view: ITypeRoute<
		UserDb.IUserItem,
		{
			edit: ITypeRouteAction<UserDb.IUserItem>;
			notifications: ITypeRouteAction<UserDb.IUserItem>;
			password: ITypeRouteAction<UserDb.IUserItem>;
			avatar: ITypeRouteAction<UserDb.IUserItem>;
			subscription: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	utilities: ITypeRoute<
		UserDb.IUserItem,
		{
			install: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
	account: ITypeRoute<
		UserDb.IUserItem,
		{
			ready: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
}

export const profileRoutes: RouteInitializer<IProfileRoutes> = {
	account: {
		mainPath: 'profile',
		uniqueId: 'profileSignup',
		loadSharedData: (context) => of(undefined),
		menuItems: (context) => [],
		parentRoutes: (context) => [],
		children: {
			ready: {
				uniqueId: `accountReady`,
				component: AccountReadyComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Account ready`,
						card: undefined
					};
					return metadata;
				}
			}
		}
	},
	utilities: {
		mainPath: 'profile',
		uniqueId: 'profileUtilities',
		loadSharedData: (context) => of(undefined),
		menuItems: (context) => [],
		parentRoutes: (context) => [],
		children: {
			install: {
				uniqueId: `install`,
				component: InstallComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Install app`,
						card: undefined
					};
					return metadata;
				}
			}
		}
	},
	view: {
		mainPath: 'profile',
		uniqueId: 'profileMain',
		parentRoutes: (context) => [],
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<UserDb.IUserItem>((builder) =>
					builder.withCustomApiRoute('user', 'getUser').withId(context.authUser.id).build()
				)
				.pipe(
					map((response) => {
						const shared: ISharedData<UserDb.IUserItem> = {
							data: response.data,
							linkedItems: response.linkedItems
						};
						return shared;
					})
				);
		},
		menuItems: (context) => {
			const menuItems: IMenuItem[] = [
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.profileRoutes.view.children.edit)
						.getUrl(),
					title: `Edit`,
					iconType: 'user_pen'
				},
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.profileRoutes.view.children.notifications)
						.getUrl(),
					title: `Notifications`,
					iconType: 'notification'
				},
				{
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.profileRoutes.view.children.password)
						.getUrl(),
					title: `Password`,
					iconType: 'password'
				}
			];

			if (context.data?.data?.userType === 'trainer') {
				menuItems.push({
					routerLink: context.dependencies.navigateService
						.actionRoute((routes) => routes.profileRoutes.view.children.subscription)
						.getUrl(),
					title: `Subscription`,
					iconType: 'purchase'
				});
			}

			return menuItems;
		},
		children: {
			subscription: {
				uniqueId: `subscription`,
				component: SubscribeComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: `Subscription`,
						card: data.sharedData ? getCard(data.sharedData.data, data.dependencies) : undefined
					};
					return metadata;
				}
			},
			avatar: {
				uniqueId: `profileAvatar`,
				component: EditMyAvatarComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'Avatar',
						card: data.sharedData ? getCard(data.sharedData.data, data.dependencies) : undefined
					};
					return metadata;
				}
			},
			edit: {
				uniqueId: `editProfile`,
				component: EditMySettingsComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'Edit',
						card: data.sharedData ? getCard(data.sharedData.data, data.dependencies) : undefined
					};
					return metadata;
				}
			},
			notifications: {
				uniqueId: `notifications`,
				component: MyNotificationsComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'Notifications',
						card: data.sharedData ? getCard(data.sharedData.data, data.dependencies) : undefined
					};
					return metadata;
				}
			},
			password: {
				uniqueId: `editPassword`,
				component: EditUserPasswordComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'Password',
						card: data.sharedData ? getCard(data.sharedData.data, data.dependencies) : undefined
					};
					return metadata;
				}
			}
		}
	}
};

function getCard(user: UserDb.IUserItem, dependencies: CoreAppComponentDependencies): ITypePageCard<UserDb.IUserItem> {
	const name = nameHelper.getUserName(user.email, user.firstName, user.lastName);
	const tags: IPageTag[] = [];

	if (user.userType === 'trainer') {
		tags.push({
			icon: 'upgrade',
			text: `${stringHelper.capitalizeText(user.license)}`
		});
	}

	tags.push(
		{
			icon: 'user',
			text: user.userType === 'trainer' ? `Trainer` : 'Client'
		},
		{
			icon: 'calendar',
			text: `Joined ${dependencies.timeService.formatDate(user.system.created)}`
		}
	);

	if (user.timezone) {
		tags.push({
			icon: 'timezone',
			text: `${user.timezone}`
		});
	}

	return {
		title: {
			value: user.fullName
		},
		subtitle: name !== user.email ? user.email : undefined,
		tags: tags,
		avatar: {
			avatar: user.avatar,
			avatarName: name,
			upload: {
				avatarFile: user?.uploadedAvatar?.[0],
				updateSharedData: (file, sharedData) => {
					if (sharedData.data) {
						sharedData.data.uploadedAvatar = file ? [file] : [];
						sharedData.data.avatar = file?.image;
						sharedData.data.system.updated = dependencies.timeService.nowCosmosDb();
					}

					return sharedData;
				},
				imageCrop: dependencies.coreAppService.defaultSquareImageCrop,
				fileUpload: (file) => {
					return dependencies.httpService
						.uploadCustomFile<CosmosDb.ICosmosFileReference>((builder) =>
							builder.withCustomApiRoute('user', 'uploadAvatar').withFile(file).build()
						)
						.pipe(
							map((response) => {
								return response.data;
							})
						);
				},
				save: (file) => {
					return dependencies.httpService
						.post<UserApi.IUserResponseWithToken, Partial<UserApi.IEditUserAvatarRequest>>((builder) =>
							builder
								.withCustomApiRoute('user', 'editAvatar')
								.withData({
									uploadedAvatar: file ? [file] : undefined
								})
								.withId(user.id)
								.build()
						)
						.pipe(
							map((response) => {
								dependencies.authService.updateAuthUser(response.data.token);
							})
						);
				}
			}
		},
		layoutStyle: 'default'
	};
}
